import { useMemo } from 'react'
import { PlayCircle } from 'lucide-react'

/**
 * Trailer do curso — iframe simples 16:9 do YouTube.
 *
 * Diferente do `YouTubePlayer` (que faz tracking de progresso pra
 * episódio), aqui é só preview: sem API JS, sem tracking, sem
 * autoplay. Apenas embed responsivo pra deixar a página de detalhe
 * mais rica quando o admin cadastra um trailer.
 *
 * URL aceita os mesmos 3 formatos que o player oficial:
 *   - https://www.youtube.com/watch?v=ID
 *   - https://youtu.be/ID
 *   - https://www.youtube.com/embed/ID
 *
 * Se a URL não casar com nenhum, o componente não renderiza nada
 * (silencioso — admin pode ter colado coisa errada e não queremos
 * espaço vazio na página).
 */
function extractYouTubeId(url: string): string | null {
  // Formato 1: ?v=ID ou &v=ID
  let m = url.match(/[?&]v=([^&]+)/)
  if (m) return m[1]
  // Formato 2: /embed/ID
  m = url.match(/embed\/([^?]+)/)
  if (m) return m[1]
  // Formato 3: youtu.be/ID
  m = url.match(/youtu\.be\/([^?]+)/)
  if (m) return m[1]
  return null
}

interface CourseTrailerProps {
  url: string
  /** Acessibility — exibido no aria-label do iframe e fallback. */
  courseTitle: string
}

export function CourseTrailer({ url, courseTitle }: CourseTrailerProps) {
  const videoId = useMemo(() => extractYouTubeId(url), [url])

  if (!videoId) return null

  return (
    <div className="relative rounded-xl overflow-hidden border border-border/50 bg-black/40 shadow-lg shadow-black/30">
      {/* Tag de identificação no canto */}
      <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-wider text-white/90 font-semibold pointer-events-none">
        <PlayCircle className="w-3 h-3" aria-hidden />
        Trailer
      </div>
      <div className="relative aspect-video">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
          title={`Trailer — ${courseTitle}`}
          aria-label={`Trailer do curso ${courseTitle}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 w-full h-full"
        />
      </div>
    </div>
  )
}
